// ABOUTME: Shared CPQ types. Single source of truth for the MODEL.item_type discriminator and for
// ABOUTME: the shape of a line item's serialized configuration and its configuration chain.

import type { TUserAssignment } from '@coolshop/cool-configurator';

export const ITEM_TYPE = { product: 'product', service: 'service' } as const;

/**
 * One Configit package view: the id ACE needs plus the label the modeler gave it. The drawer reads
 * the list off the apply-assignments response, which carries the views of the model it resolved.
 */
export interface AceView {
    id: string;
    label: string;
}

export type ItemType = (typeof ITEM_TYPE)[keyof typeof ITEM_TYPE];

/**
 * What the configurator serializes into a line item's `configuration_json`: the selections plus the
 * product and prototype they belong to.
 */
export interface LineItemConfiguration {
    userAssignments: TUserAssignment[];
    productId: string;
    prototypeId: string;
    /**
     * Configit package view the configuration was made against. Absent on single-view models and on
     * every line item written before views existed, which re-resolve under the model's default view.
     */
    viewId?: string;
}

/**
 * A catalog-category section of an offer's line items, as the offer endpoints return it when the
 * tenant groups them (cpq.groupLineItemsByCategory). `lineItemIds` are in display order.
 */
export interface LineItemSection {
    /** The catalog (MODEL_SELECTION) node; null for the lines no category is known for. */
    categoryId: string | null;
    title: string | null;
    lineItemIds: string[];
}

/**
 * One step of a line item's configuration chain, as stored in `chain_json`. The chain is ordered
 * and its last step is the effective configuration: the one that prices the line item and the one
 * `configuration_json` holds.
 */
export interface ChainStep {
    productId: string;
    prototypeId: string;
    configurationJson: string;
}

export function serializeLineItemConfiguration(configuration: LineItemConfiguration): string {
    return JSON.stringify(configuration);
}

/** Returns null for an absent or malformed configuration, or one without a prototype to open. */
export function parseLineItemConfiguration(
    configurationJson: string | undefined | null,
): LineItemConfiguration | null {
    if (!configurationJson) return null;
    try {
        const parsed = JSON.parse(configurationJson);
        if (!parsed || typeof parsed.prototypeId !== 'string') return null;
        return {
            userAssignments: Array.isArray(parsed.userAssignments) ? parsed.userAssignments : [],
            productId: typeof parsed.productId === 'string' ? parsed.productId : '',
            prototypeId: parsed.prototypeId,
            ...(typeof parsed.viewId === 'string' ? { viewId: parsed.viewId } : {}),
        };
    } catch {
        return null;
    }
}

/**
 * Reads a line item's chain, falling back to a one-step chain over its `configuration_json`.
 * The fallback covers line items written before `chain_json` existed, and any malformed value.
 * Returns an empty chain for a line item with no configuration at all.
 */
export function parseChain(
    chainJson: string | undefined | null,
    configurationJson: string | undefined | null,
): ChainStep[] {
    const parsed = (() => {
        if (!chainJson) return null;
        try {
            return JSON.parse(chainJson);
        } catch {
            return null;
        }
    })();

    if (Array.isArray(parsed)) {
        const steps = parsed.filter(
            (step): step is ChainStep =>
                Boolean(step) &&
                typeof step.prototypeId === 'string' &&
                typeof step.configurationJson === 'string',
        );
        if (steps.length > 0) return steps;
    }

    const configuration = parseLineItemConfiguration(configurationJson);
    if (!configuration) return [];
    return [
        {
            productId: configuration.productId,
            prototypeId: configuration.prototypeId,
            configurationJson: configurationJson as string,
        },
    ];
}
